<script lang="ts">
  import { onMount } from 'svelte'
  import { tStore } from '../i18n'
  import { sourcesApi, type Source } from '../api/sources'

  interface Props {
    selected: number[]
  }

  let { selected = $bindable() }: Props = $props()

  let sources = $state<Source[]>([])
  let loading = $state(true)
  let errorMsg = $state<string | null>(null)

  onMount(() => {
    void load()
  })

  async function load() {
    loading = true
    errorMsg = null
    try {
      sources = await sourcesApi.list()
    } catch (err) {
      errorMsg = err instanceof Error ? err.message : String(err)
    } finally {
      loading = false
    }
  }

  const selectedSet = $derived(new Set(selected))

  function toggle(id: number) {
    selected = selectedSet.has(id) ? selected.filter((x) => x !== id) : [...selected, id]
  }
</script>

<div class="picker">
  {#if loading}
    <p class="muted">{$tStore('common.loading')}</p>
  {:else if errorMsg}
    <p class="error">{errorMsg}</p>
  {:else if sources.length === 0}
    <p class="muted">{$tStore('components.platformPicker.noPlatforms')}</p>
  {:else}
    <ul class="picker__rows">
      {#each sources as source (source.id)}
        <li>
          <label class="picker__row">
            <input
              type="checkbox"
              checked={selectedSet.has(source.id)}
              onchange={() => toggle(source.id)}
            />
            <span class="picker__row-label">{source.displayName}</span>
            {#if !source.enabled}
              <span class="badge badge--muted">{$tStore('components.platformPicker.disabled')}</span
              >
            {/if}
          </label>
        </li>
      {/each}
    </ul>
  {/if}

  <p class="picker__count">
    {$tStore('components.platformPicker.selectedCount', { count: selected.length })}
  </p>
</div>

<style lang="scss">
  @use '../assets/styles/variables' as v;

  .picker {
    display: flex;
    flex-direction: column;
    gap: v.$space-sm;
    border: 1px solid v.$color-border;
    border-radius: v.$radius-md;
    padding: v.$space-md;
    background: v.$color-surface;
  }

  .picker__rows {
    display: flex;
    flex-direction: column;
    gap: v.$space-xs;
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .picker__row {
    display: flex;
    align-items: center;
    gap: v.$space-xs;
    font-size: 0.8125rem;
    cursor: pointer;
  }

  .picker__row-label {
    color: v.$color-text;
  }

  .picker__count {
    margin: 0;
    font-size: 0.75rem;
    color: v.$color-text-muted;
  }

  .badge--muted {
    display: inline-block;
    padding: 0.1rem 0.5rem;
    border-radius: v.$radius-pill;
    background: v.$color-border;
    color: v.$color-text-muted;
    font-size: 0.6875rem;
    font-weight: 600;
    text-transform: uppercase;
  }
</style>
